import { addMonths, parse, subMonths, subSeconds } from 'date-fns/esm'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectAggregateDepotsByLatLng, selectDepots } from '@/features/domain/depot'
import { selectOrderCustomFields, selectOrders } from '@/features/domain/order'
import { selectTerritory } from '@/features/domain/territory'
import {
  selectUserAccount,
  selectUserAccountPreferences,
  selectUserConfiguration,
  selectUserProfile,
} from '@/features/domain/user'

import { useEnvironmentQa, useResetEditingState } from '@/atoms'
import { selectCompanies } from '@/features/domain/company'
import { selectRecurrences } from '@/features/domain/routeplan'
import { selectCalendarRange } from '@/features/domain/ui'
import {
  selectLoadsFromVehicles,
  selectSortedRMVehicles,
  selectTagsFromVehicles,
} from '@/features/domain/vehicle'
import { useReadOnly, useValidateEmail } from '@/hooks'
import { removeInspectPin, useGetMapInfo, useInspectPin } from '@/map'

import { TabValues } from '../../../typings'

import { EditOrderStep } from './EditOrderStep'
import { useOnCreateOrder, useOnUpdateOrder } from './hooks'

type Props = {
  order: uui.domain.client.rm.ExtendedOrderStep
  tab: TabValues
}

export function EditOrderStepProvider(props: Props) {
  const { today, planType, distanceFormat, weekStartsOn, timeFormat } =
    useSelector(selectUserConfiguration)

  const { minDate } = useSelector(selectCalendarRange)
  const userProfile = useSelector(selectUserProfile)
  const {
    name,
    workingDayStartSec,
    notificationBehavior,
    licensingLimits: { orderRadiusMt, planningRange, trackedVehicles },
  } = useSelector(selectTerritory)
  const recurrences = useSelector(selectRecurrences)

  const { maxCustomFieldsPerOrder, maxTags } = useSelector(selectUserAccount).licensingLimits
  const depotsAsMapByLatLng = useSelector(selectAggregateDepotsByLatLng)
  const customFields = useSelector(selectOrderCustomFields)
  const vehicles = useSelector(selectSortedRMVehicles)
  const loads = useSelector(selectLoadsFromVehicles)
  const tags = useSelector(selectTagsFromVehicles)
  const { allowEmptyCustomFields } = useSelector(selectUserAccountPreferences)
  const resetEditingState = useResetEditingState()
  const { readonly } = useReadOnly()
  const orders = useSelector(selectOrders)
  const depots = useSelector(selectDepots)
  const qa = useEnvironmentQa()
  const companies = useSelector(selectCompanies)

  const { onCreateOrder, submitting: createSubmitting } = useOnCreateOrder()
  const { onUpdateOrder, submitting: updateSubmitting } = useOnUpdateOrder(props.order?.id)

  const todayDate = useMemo(() => parse(today, 'yyyyMMdd', new Date()), [today])

  const todayMinusMidnightCross = useMemo(() => {
    return subSeconds(todayDate, workingDayStartSec)
  }, [todayDate, workingDayStartSec])

  const calendarMinDate = useMemo(() => parse(minDate, 'yyyyMMdd', new Date()), [minDate])

  const forcedCalendarRange = useMemo(() => {
    return {
      min: subMonths(todayDate, 120),
      max: addMonths(todayDate, 120),
    }
  }, [todayDate])

  const handleStopEditing = useCallback(() => {
    resetEditingState()
  }, [resetEditingState])

  const isSimulation = planType === 'simulation'

  const [inspectPin] = useInspectPin()
  const initialLatLngFromMapPin =
    inspectPin?.mode === 'converted' && inspectPin.target === 'order'
      ? inspectPin.latLng
      : undefined

  const { center: mapCenter } = useGetMapInfo()

  const validateEmail = useValidateEmail()

  return (
    <EditOrderStep
      qa={qa}
      tags={tags}
      loads={loads}
      tab={props.tab}
      orders={orders}
      depots={depots}
      order={props.order}
      vehicles={vehicles}
      territoryName={name}
      mapCenter={mapCenter}
      planLocked={readonly}
      companies={companies}
      maxTagsCount={maxTags}
      customFields={customFields}
      weekStartsOn={weekStartsOn}
      createOrder={onCreateOrder}
      h24={timeFormat === 'HH:mm'}
      updateOrders={onUpdateOrder}
      validateEmail={validateEmail}
      today={todayMinusMidnightCross}
      territoryRadius={orderRadiusMt}
      recurrences={recurrences}
      stopEditing={handleStopEditing}
      allowBarcodes={trackedVehicles > 0}
      removeInspectPin={removeInspectPin}
      workingDayStartSec={workingDayStartSec}
      useMetric={distanceFormat === 'METRIC'}
      depotsAsMapByLatLng={depotsAsMapByLatLng}
      notificationBehavior={notificationBehavior}
      allowEmptyCustomFields={allowEmptyCustomFields}
      maxCustomFieldsPerOrder={maxCustomFieldsPerOrder}
      submitting={createSubmitting || updateSubmitting}
      planningRange={isSimulation ? undefined : planningRange}
      initialLatLngFromInspectPin={initialLatLngFromMapPin}
      lockDate={isSimulation ? undefined : calendarMinDate}
      maxBarcodes={userProfile.account.licensingLimits.maxBarcodes}
      forcedCalendarRange={isSimulation ? undefined : forcedCalendarRange}
      forcedInteractiveRange={isSimulation ? undefined : forcedCalendarRange}
      isSimulation={isSimulation}
    />
  )
}
